import type {ReactNode} from 'react';

import {
  Button,
  Modal,
  Table,
  TBody,
  Td,
  TextArea,
  Th,
  Thead,
  Tr,
} from '@/components';

const MODAL_WIDTH = 'w-10/12 sm:w-10/12 md:w-7/12 lg:w-4/12';

export type PriceChangeRow = {
  key: string;
  corridor: string;
  from: string;
  to: string;
};

/** Corridors with the price they have now and the price they would move to. */
const PriceChangeTable = ({
  rows,
  fromHeader,
  toHeader,
}: {
  rows: PriceChangeRow[];
  fromHeader: string;
  toHeader: string;
}) => (
  <Table>
    <Thead>
      <Tr enableHover={false}>
        <Th className="px-3">Corridor</Th>
        <Th className="px-3">{fromHeader}</Th>
        <Th className="px-3">{toHeader}</Th>
      </Tr>
    </Thead>
    <TBody>
      {rows.map((row) => (
        <Tr key={row.key}>
          <Td className="px-3">{row.corridor}</Td>
          <Td className="px-3">{row.from}</Td>
          <Td className="px-3 font-semibold">{row.to}</Td>
        </Tr>
      ))}
    </TBody>
  </Table>
);

type ConfirmFooterProps = {
  confirmLabel: string;
  loadingText: string;
  colorScheme?: 'cyan' | 'red';
  isLoading: boolean;
  disabled?: boolean;
  onBack: () => void;
  onConfirm: () => void;
};

const ConfirmFooter = ({
  confirmLabel,
  loadingText,
  colorScheme = 'cyan',
  isLoading,
  disabled,
  onBack,
  onConfirm,
}: ConfirmFooterProps) => (
  <div className="flex justify-end gap-3">
    <Button
      type="button"
      variant="ghost"
      colorScheme="cyan"
      onClick={onBack}
      disabled={isLoading}
    >
      Back
    </Button>
    <Button
      type="button"
      colorScheme={colorScheme}
      onClick={onConfirm}
      isLoading={isLoading}
      loadingText={loadingText}
      disabled={disabled}
    >
      {confirmLabel}
    </Button>
  </div>
);

type ModalShellProps = {
  title: string;
  footer: ReactNode;
  error: string | null;
  isLoading: boolean;
  onClose: () => void;
  children: ReactNode;
};

const ModalShell = ({
  title,
  footer,
  error,
  isLoading,
  onClose,
  children,
}: ModalShellProps) => (
  <Modal
    isOpen
    onClose={onClose}
    title={title}
    width={MODAL_WIDTH}
    closeOnOutsideClick={!isLoading}
    hideX={isLoading}
    footer={footer}
  >
    <div className="flex flex-col gap-4">
      {children}
      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : null}
    </div>
  </Modal>
);

type CommonModalProps = {
  name: string;
  isLoading: boolean;
  error: string | null;
  onBack: () => void;
  onConfirm: () => void;
};

const corridorCount = (count: number) =>
  `${count} ${count === 1 ? 'corridor' : 'corridors'}`;

export const AcceptPricingModal = ({
  name,
  rows,
  ...rest
}: CommonModalProps & {rows: PriceChangeRow[]}) => (
  <ModalShell
    title={`Accept ${name}'s pricing`}
    error={rest.error}
    isLoading={rest.isLoading}
    onClose={rest.onBack}
    footer={
      <ConfirmFooter
        confirmLabel="Accept pricing"
        loadingText="Accepting..."
        isLoading={rest.isLoading}
        onBack={rest.onBack}
        onConfirm={rest.onConfirm}
      />
    }
  >
    <p className="text-sm text-gray-600 dark:text-gray-300">
      {`This pricing goes live for ${name} as soon as you accept, across ${corridorCount(rows.length)}. Every other business keeps the default fees.`}
    </p>
    <PriceChangeTable rows={rows} fromHeader="Now" toHeader="New" />
  </ModalShell>
);

export const SendCounterModal = ({
  name,
  rows,
  ...rest
}: CommonModalProps & {rows: PriceChangeRow[]}) => (
  <ModalShell
    title={`Send counter to ${name}`}
    error={rest.error}
    isLoading={rest.isLoading}
    onClose={rest.onBack}
    footer={
      <ConfirmFooter
        confirmLabel="Send counter"
        loadingText="Sending..."
        isLoading={rest.isLoading}
        onBack={rest.onBack}
        onConfirm={rest.onConfirm}
      />
    }
  >
    <p className="text-sm text-gray-600 dark:text-gray-300">
      {`${name} sees these prices and can accept, counter or withdraw. Nothing changes until both sides agree.`}
    </p>
    <PriceChangeTable
      rows={rows}
      fromHeader="Merchant asks"
      toHeader="Your price"
    />
  </ModalShell>
);

export const DeclinePricingModal = ({
  name,
  reason,
  onReasonChange,
  ...rest
}: CommonModalProps & {
  reason: string;
  onReasonChange: (reason: string) => void;
}) => (
  <ModalShell
    title={`Decline ${name}'s pricing request`}
    error={rest.error}
    isLoading={rest.isLoading}
    onClose={rest.onBack}
    footer={
      <ConfirmFooter
        confirmLabel="Decline pricing request"
        loadingText="Declining..."
        colorScheme="red"
        isLoading={rest.isLoading}
        disabled={!reason.trim()}
        onBack={rest.onBack}
        onConfirm={rest.onConfirm}
      />
    }
  >
    <p className="text-sm text-gray-600 dark:text-gray-300">
      {`${name} stays on its current fees and is emailed your reason.`}
    </p>
    <TextArea
      label="Reason (shared with the merchant)"
      name="pricingDeclineReason"
      value={reason}
      onChange={(event) => onReasonChange(event.target.value)}
      placeholder="Explain why this request is being declined"
    />
  </ModalShell>
);

export const ApplyPricingModal = ({
  name,
  count,
  ...rest
}: CommonModalProps & {count: number}) => (
  <ModalShell
    title={`Apply ${name}'s pricing`}
    error={rest.error}
    isLoading={rest.isLoading}
    onClose={rest.onBack}
    footer={
      <ConfirmFooter
        confirmLabel="Apply pricing"
        loadingText="Applying..."
        isLoading={rest.isLoading}
        onBack={rest.onBack}
        onConfirm={rest.onConfirm}
      />
    }
  >
    <p className="text-sm text-gray-600 dark:text-gray-300">
      {`This saves the agreed pricing for ${name}, across ${corridorCount(count)}. Until then ${name} stays on the default fees.`}
    </p>
  </ModalShell>
);
